import { useCallback } from 'react';
import useSelectedPatient from '../patient/useSelectedPatient';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { dicomProcessorState } from '~/state/atoms/dicomProcessorAtoms';
import useViewportEnvironment from '../useViewportEnvironment';
import { Image3D } from '~/domain/patient/image3d';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import {
    hasUnsavedChangesState,
    patientRepositoryState,
} from '~/state/atoms/patientAtoms';
import { DicomProcessor } from '~/domain/services/dicomProcessor';
import { exportableItemsCountState } from '~/state/atoms/viewerAtoms';

/**
 * A custom hook that provides a function to create a new empty structure set
 * for the currently selected patient.
 *
 * @returns A callback function to create a new structure set in the current patient.
 */
export default function useCreateStructureSet() {
    const dicomProcessor = useRecoilValue(dicomProcessorState);
    const primaryImage = useRecoilValue(primaryImageState);
    const patient = useSelectedPatient();
    const patientRepository = useRecoilValue(patientRepositoryState);
    const { setEnvironmentStructureSet } = useViewportEnvironment();
    const setExportableItemsCount = useSetRecoilState(
        exportableItemsCountState
    );
    const setHasUnsavedChanges = useSetRecoilState(hasUnsavedChangesState);
    const createStructureSet = useCallback(
        (
            /** Optional image to associate the new structure set with.
             * If undefined, the current primary image will be used. */
            image: Image3D | undefined = undefined,
            /** True if newly created structure set should immediately be set active, false otherwise.
             * Action is not performed if the target image is not current primary image. Defaults to true. */
            setCreatedActive = true
        ) => {
            const targetImage = image || primaryImage;

            if (!patient) {
                console.error(
                    'Cannot create structure set: No patient selected'
                );
                return;
            }

            if (!targetImage) {
                console.error(
                    'Cannot create structure set: No primary image selected'
                );
                return;
            }

            // Create the DICOM structure set
            const dicomStructureSet =
                DicomProcessor.createEmptyDefaultDicomStructureSet(
                    patient,
                    targetImage
                );

            // Register in DicomProcessor's internal data and process
            // through the full pipeline (same path as imported files)
            dicomProcessor.updatePatientDataset(
                dicomStructureSet.datasetWrapper.dataset,
                dicomStructureSet
            );
            const result = dicomProcessor.toRepository(patientRepository);
            setExportableItemsCount((prevCount) => prevCount + 1);
            setHasUnsavedChanges(true);

            if (setCreatedActive && targetImage === primaryImage) {
                setEnvironmentStructureSet(
                    result.structureSets,
                    patient.patientId
                );
            }
        },
        [
            primaryImage,
            patient,
            dicomProcessor,
            patientRepository,
            setExportableItemsCount,
            setHasUnsavedChanges,
            setEnvironmentStructureSet,
        ]
    );

    return createStructureSet;
}
